import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Lightbulb, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useAvailableHelpFeatures } from '@/hooks/useAvailableHelpFeatures';
import { useTipPrefs, today } from '@/hooks/useTipPrefs';
import { getFeatureUsage, type FeatureUsage } from '@/lib/api';
import { SUGGESTED_TIPS } from '@/lib/helpContent';
import { Keys } from '@/components/HelpPanel';

/** Card switches itself off after this many tips in a row expire without any interaction */
const MAX_IGNORED_IN_A_ROW = 2;
/** A tip stays on the Home card for at most this many days */
const CARD_TIP_DAYS = 2;
/** A new tip is introduced at most once every this many days ("Next tip" skips the wait) */
const NEW_TIP_GAP_DAYS = 3;

function daysSince(ymd: string) {
  if (!ymd) return Infinity;
  const [y, m, d] = ymd.split('-').map(Number);
  const then = new Date(y, m - 1, d).getTime();
  const [ty, tm, td] = today().split('-').map(Number);
  return Math.round((new Date(ty, tm - 1, td).getTime() - then) / 86_400_000);
}

/**
 * "Get more out of OneCheckUp": suggests one feature the user hasn't used yet.
 * Only suggests features they can use (plan/role/permission) and skips anything
 * the usage endpoint says they've already used.
 *
 * Pacing, so it never nags:
 *  - a tip shows on at most CARD_TIP_DAYS days, then retires by itself
 *  - a new tip is introduced at most every NEW_TIP_GAP_DAYS days ("Next tip" skips the wait)
 *  - opening search retires the Quick search tip (see SearchOverlay)
 *  - if tips keep expiring unnoticed, the card turns itself off (help stays one click away)
 */
export default function FeatureTipCard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const available = useAvailableHelpFeatures();
  const { prefs, update, recordView, turnOff, isActive } = useTipPrefs();
  const [usage, setUsage] = useState<FeatureUsage | null>(null);
  const hidden = !currentUser || prefs.off || prefs.cardOff;

  useEffect(() => {
    if (hidden) return;
    getFeatureUsage().then(setUsage).catch(() => {});
  }, [currentUser?.id, hidden]);

  const candidates = usage
    ? SUGGESTED_TIPS
        .filter(t => !(t.usedWhen && usage[t.usedWhen]))
        .map(t => available.find(f => f.id === t.id))
        .filter((f): f is NonNullable<typeof f> => !!f && isActive(f.id))
        .filter(f => {
          const days = prefs.views[f.id] || [];
          return days.includes(today()) || days.length < CARD_TIP_DAYS;
        })
    : [];
  const candidateIds = candidates.map(f => f.id).join(',');
  const tip = candidates.find(f => f.id === prefs.cardTip);

  // Keep the current tip, or decide whether a new one may be introduced today
  useEffect(() => {
    if (hidden || !usage) return;
    if (tip) { recordView(tip.id); return; }

    const t = today();
    update(p => {
      const prev = p.cardTip;
      const used = SUGGESTED_TIPS.find(s => s.id === prev)?.usedWhen;
      // The previous tip vanished without being dismissed or used → it expired unnoticed
      const ignored = prev && !p.seen.includes(prev) && !(used && usage[used]) ? p.cardIgnored + 1 : p.cardIgnored;
      if (ignored >= MAX_IGNORED_IN_A_ROW) return { ...p, cardTip: null, cardIgnored: ignored, cardOff: true };

      const next = daysSince(p.cardDay) < NEW_TIP_GAP_DAYS ? undefined : candidates[0];
      if (!next) return prev === null && ignored === p.cardIgnored ? p : { ...p, cardTip: null, cardIgnored: ignored };
      return {
        ...p, cardTip: next.id, cardDay: t, cardIgnored: ignored,
        views: { ...p.views, [next.id]: [...(p.views[next.id] || []), t] },
      };
    });
  }, [hidden, usage, tip?.id, candidateIds, prefs.cardTip, prefs.cardDay]);

  if (hidden || !tip) return null;

  // Any interaction shows the card is welcome, so reset the ignored streak
  const retire = (allowNextNow: boolean) => update(p => ({
    ...p,
    seen: p.seen.includes(tip.id) ? p.seen : [...p.seen, tip.id],
    cardTip: null,
    cardIgnored: 0,
    cardDay: allowNextNow ? '' : p.cardDay,
  }));

  const tryIt = () => {
    retire(false);
    if (!tip.to) return;
    if (tip.newWindow) window.open(tip.to, '_blank');
    else navigate(tip.to);
  };

  return (
    <div className="mb-6 flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-border bg-card/70 px-4 py-2.5">
      <Lightbulb className="w-4 h-4 text-warning shrink-0" aria-hidden />
      <p className="min-w-0 flex-1 text-sm text-muted-foreground" title={tip.how}>
        <span className="sr-only">Tip: </span>
        <span className="font-medium text-foreground">{tip.title}</span>
        {tip.shortcut && <span className="mx-1.5 inline-flex align-middle"><Keys keys={tip.shortcut} /></span>}
        <span className="hidden sm:inline"> — </span><span className="block sm:inline">{tip.benefit}</span>
      </p>
      <div className="flex items-center gap-3 shrink-0 ml-7 sm:ml-0">
        {tip.to && (
          <button onClick={tryIt} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
            Try it <ArrowRight className="w-3 h-3" />
          </button>
        )}
        <button onClick={() => retire(true)} className="text-xs text-muted-foreground hover:text-foreground">Next tip</button>
        <button onClick={turnOff} className="p-1 -m-1 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground" title="Turn off tips" aria-label="Turn off tips">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
